/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, May 29th 2024, 7:40:44 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FormMany2OnePicker, FormInfoPopUpForm } from "form-builder/components";
import { FORM_STATUS_CHOICES_ENUM, TaskTemplate } from "lib/schemas";
import { ProcessStagesTransferListInput } from "service-builder/components/stages/ProcessStageInput";
import { useServiceBuilderContext } from "service-builder/providers";
import { TranslatableTextFormInput } from "translation/components";
import { SwitchFormInput } from "ui-widgets/forms";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { InputHelpDialogWrapper, EmphasisText } from "ui-widgets";
import { TaskTemplatesTransferListInput } from "./TaskTemplateInput";

export const TaskUndoSettings = () => {
  const { watch } = useOSFormContext<TaskTemplate>();
  const [taskTemplateId, taskTitle] = watch<(keyof TaskTemplate)[]>(["id", "title"]) as [
    TaskTemplate["id"],
    TaskTemplate["title"]
  ];

  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  return (
    <>
      <h3>
        <EmphasisText>Undo Settings</EmphasisText>
      </h3>

      <InputHelpDialogWrapper
        helpTitle="Can be undone?"
        helpContent="If set to true, this task can be undone once executed. This setting is useful for tasks that can be reversed."
      >
        <SwitchFormInput<TaskTemplate>
          schema_field_key="can_be_undone"
          label="Can this task be undone once executed?"
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Undo Title"
        helpContent="The call to action that will be displayed to the user when they are able to undo this task."
      >
        <TranslatableTextFormInput<TaskTemplate> schema_field_key="undo_title" label="Undo Title" />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Undo Action Form"
        helpContent="Select a custom form that the user must fill in when they undo this task."
      >
        <FormMany2OnePicker<TaskTemplate>
          schema_field_key="undo_action_form"
          label="Undo Action Form"
          apiQueryParams={{ form_type: "general_form" }}
          renderRelatedObjectForm={({ updatePrimaryObject, closeNewFormFn, newFormOpenState }) => (
            <div style={{ marginRight: "100px" }}>
              <FormInfoPopUpForm
                closeFormInfoPopUpFn={closeNewFormFn}
                formInfoPopUpOpenState={newFormOpenState}
                defaultNewFormConfiguration={{
                  artifact_org: currentProcessDefinition.artifact_org,
                  form_type: "general_form",
                  title: `${taskTitle} Undo Form`,
                  status: FORM_STATUS_CHOICES_ENUM.Published,
                  login_required: true,
                  sites: [currentProcessDefinition.site],
                }}
                popUpTitle="New Undo Action Form"
                onAPISaveFormInfoCallBackFn={(formConfiguration) => updatePrimaryObject(formConfiguration.id)}
              />
            </div>
          )}
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Owner Can Undo"
        helpContent="If set to true, the owner of this task can undo it even if they do not have explicit permission to undo tasks."
      >
        <SwitchFormInput<TaskTemplate>
          schema_field_key="task_owner_can_undo"
          label="Can the owner of this task undo it?"
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Limit Undo to Stages"
        helpContent="Select the stages in which this task can be undone. If left empty, the task can be undone at any stage."
      >
        <ProcessStagesTransferListInput<TaskTemplate>
          schema_field_key="allowed_undo_stages"
          label="Limit Undo to Stages"
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Tasks to Cancel on Undo"
        helpContent="The specific active user tasks that you choose here will be cancelled when this task is undone."
      >
        <TaskTemplatesTransferListInput<TaskTemplate>
          schema_field_key="tasks_to_cancel_on_undo"
          apiQueryParams={{
            process_definition: currentProcessDefinition?.id,
            type: "View",
            exclude: [taskTemplateId],
          }}
          label="Choose specific active tasks to cancel when this task is undone"
        />
      </InputHelpDialogWrapper>

      <h3>
        <EmphasisText>State Preservation</EmphasisText>
      </h3>
      <InputHelpDialogWrapper
        helpTitle="Preserve Owner"
        helpContent="If set to true, the previous owner of this task will be restored when the task is undone."
      >
        <SwitchFormInput
          schema_field_key="settings_for_undone_state_preservation.preserve_owner"
          label="Preserve Owner"
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Preserve Assessment Input"
        helpContent="If set to true, the assesment input carried out by the task owner will be preserved when the task is undone."
      >
        <SwitchFormInput
          schema_field_key="settings_for_undone_state_preservation.preserve_review_form_entry"
          label="Preserve Assessment Input"
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Reactivate Cancelled Tasks In Same Stage"
        helpContent="If set to true, any cancelled tasks in the same stage as this task will be reactivated."
      >
        <SwitchFormInput
          schema_field_key="settings_for_undone_state_preservation.reactivate_cancelled_tasks_for_stage"
          label="Reactivate Cancelled Tasks"
        />
      </InputHelpDialogWrapper>
    </>
  );
};
